import Avatar from '@/components/Avatar'
import Badge from '@/components/Badge'
import IcoMoon from '@/components/IcoMoon'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { useFeatureToggle } from '@/context/featureToggles'
import { NavigateToGuideAction, SessionNotesInfoPageSection } from '@/shared/api-generated-types'
import FeedbackWidget from '@/widgets/Feedback'
import dayjs from 'dayjs'
import * as styles from './style'

export interface Props extends SessionNotesInfoPageSection {
  className?: string
}

function sessionNotesMonthDay(start: string): string {
  return dayjs(start).format('D MMM YYYY,')
}

function sessionNotesStartEndTime(start: string, end: string): string {
  return `${dayjs(start).format('hh:mmA')} - ${dayjs(end).format('hh:mmA')}`
}

const GuideByline = ({
  guideAvatarUrl,
  guideByline,
  navigateToGuideAction,
}: {
  guideAvatarUrl?: string
  guideByline: string
  navigateToGuideAction?: NavigateToGuideAction
}): JSX.Element | null => {
  return navigateToGuideAction?.url && guideAvatarUrl && guideByline ? (
    <span
      className={styles.byline}
      onClick={(e) => {
        e.preventDefault()
        window.location.href = navigateToGuideAction.url
      }}
    >
      <Text className="flex-center" tag="div">
        <Avatar size="xs" src={guideAvatarUrl} className={`${styles.avatar} pr-sm`} />
        <Text
          size="xxs"
          responsiveSize={{ md: 'xs' }}
          weight="md"
          className={`${styles.guideTitle} pl-xxs ellipsis-1L`}
        >
          {guideByline}
        </Text>
      </Text>
    </span>
  ) : guideAvatarUrl && guideByline ? (
    <Text className="flex-center" tag="div">
      <Avatar size="xs" src={guideAvatarUrl} className={`${styles.avatar} pr-sm`} />
      <Text size="xxs" responsiveSize={{ md: 'xs' }} weight="md" className={`${styles.guideTitle} pl-xxs`}>
        {guideByline}
      </Text>
    </Text>
  ) : null
}

const NotesInfoBlock = ({
  content: { classTitle, imageUrl, guideAvatarUrl, guideByline, navigateToGuideAction, start, end, sessionId },
}: Props): JSX.Element => {
  const showClassNotesFeedbackLinks = useFeatureToggle('class_notes_feedback_links')

  return (
    <Stack y>
      <Stack className={`${styles.imageContainer} pb-md pb-lg-gte-tablet`}>
        <div style={{ position: 'relative' }}>
          <img src={imageUrl} alt={classTitle} className={styles.image} loading="lazy" />
          <div className={styles.badge}>
            <Badge inFocus theme="FULL" size="MD" type="CLASS_NOTES" />
          </div>
        </div>
      </Stack>

      <Text size="md" responsiveSize={{ xs: 'lg', lg: 'xl' }} weight="lg" tag="div" className={styles.classTitle}>
        {classTitle}
      </Text>
      <Stack x alignItems="center" className={styles.info}>
        <GuideByline {...{ guideAvatarUrl, guideByline, navigateToGuideAction }} />
        <Stack x justifyContent="flex-start">
          <Text className="d-flex mr-xxs" tag="strong" weight="lg">
            <IcoMoon icon="calendar" size={22} />
          </Text>
          <Text className="mr-xxs" size="xxs" responsiveSize={{ md: 'xs' }} tag="strong" weight="lg">
            {sessionNotesMonthDay(start)}
          </Text>
          <Text size="xxs" responsiveSize={{ md: 'xs' }} weight="sm">
            {sessionNotesStartEndTime(start, end)}
          </Text>
        </Stack>
      </Stack>
      <div className={styles.notesBadge}>
        <Badge inFocus theme="FULL" size="MD" type="CLASS_NOTES" />
      </div>
      {showClassNotesFeedbackLinks && <FeedbackWidget sessionId={sessionId} />}
    </Stack>
  )
}

export default NotesInfoBlock
